import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SegmentedTabs } from "@akuna/ui";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { TeamAccessDenied } from "./TeamAccessDenied";
import { TeamCommissionsTab } from "./TeamCommissionsTab";
import { TeamPeopleTab } from "./TeamPeopleTab";

export type TeamTab = "people" | "commissions";

export const TEAM_TABS: Array<{ key: TeamTab; path: string }> = [
  { key: "people", path: "/team" },
  { key: "commissions", path: "/team/commissions" },
];

export default function TeamPage({ tab = "people" }: { tab?: TeamTab }) {
  const { t } = useTranslation("team");
  const navigate = useNavigate();
  const { hasPermission: canView, isLoading } = useHasPermissionSync("team", "view");

  if (isLoading) return null;
  if (!canView && tab === "people") return <TeamAccessDenied />;
  const visibleTabs = TEAM_TABS.filter((item) => item.key !== "people" || canView);

  return (
    <div className="space-y-6">
      <div className="hidden md:block">
        <h1 className="text-3xl font-bold text-foreground">{t("page.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("page.subtitle")}</p>
      </div>
      {visibleTabs.length > 1 && (
        <div className="w-full md:w-fit">
          <SegmentedTabs
            className="w-full"
            variant="outline"
            value={tab}
            onValueChange={(value) => {
              const target = TEAM_TABS.find((item) => item.key === value);
              if (target) navigate(target.path);
            }}
            items={visibleTabs.map((item) => ({ value: item.key, label: t(`page.tabs.${item.key}`) }))}
          />
        </div>
      )}
      {tab === "people" && <TeamPeopleTab />}
      {tab === "commissions" && <TeamCommissionsTab />}
    </div>
  );
}
